import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import userProgramsService, { SavedResourceLink } from 'services/userProgramsService';
import LinkIcon from '@mui/icons-material/Link';
import { isValidHttpOrHttpsUrl, normalizeResourceUrl } from '../utils/resourceLinkValidation';

interface ResourceLinkFormDropdownProps {
  onClose?: () => void;
  programManagerId?: number;
  /** Full metadata object so notes and other keys are preserved on PATCH. */
  existingMetadata?: Record<string, unknown>;
  currentLinks: SavedResourceLink[];
  onSaved?: (list: SavedResourceLink[]) => void;
}

const ResourceLinkFormDropdown: React.FC<ResourceLinkFormDropdownProps> = ({
  onClose,
  programManagerId,
  existingMetadata,
  currentLinks,
  onSaved,
}) => {
  const [linkType, setLinkType] = useState('');
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const urlNormalized = useMemo(() => normalizeResourceUrl(url), [url]);
  const urlValid = useMemo(() => isValidHttpOrHttpsUrl(url), [url]);
  const linkTypeOk = linkType.trim().length > 0;
  const canSave = linkTypeOk && urlValid && !saving;

  const handleSave = useCallback(async () => {
    if (programManagerId == null) {
      toast.error('Add this program to Program Manager before saving links.');
      return;
    }
    const typeTrim = linkType.trim();
    if (!typeTrim) {
      toast.error('Enter a link type.');
      return;
    }
    if (!isValidHttpOrHttpsUrl(url)) {
      toast.error('Enter a valid URL (http or https).');
      return;
    }
    const urlNorm = normalizeResourceUrl(url);

    const next: SavedResourceLink = {
      typeKey: typeTrim,
      url: urlNorm,
      label: typeTrim,
    };

    setSaving(true);
    try {
      const updatedList = [...currentLinks, next];
      await userProgramsService.patchProgramsManager(programManagerId, {
        metadata: {
          ...(existingMetadata || {}),
          resourceLinks: updatedList,
        },
      });
      toast.success('Resource link saved.');
      onSaved?.(updatedList);
      setLinkType('');
      setUrl('');
      onClose?.();
    } catch {
      // http interceptor
    } finally {
      setSaving(false);
    }
  }, [programManagerId, existingMetadata, currentLinks, linkType, url, onSaved, onClose]);

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '15px',
    color: '#111827',
    boxSizing: 'border-box' as const,
  };

  const labelStyle = {
    display: 'block' as const,
    color: '#111827',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '8px',
    lineHeight: 1.5,
  };

  const urlTouchedInvalid = url.trim().length > 0 && !urlValid;

  return (
    <div
      style={{
        backgroundColor: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '16px',
      }}
    >
      <div style={{ marginBottom: '14px' }}>
        <label htmlFor="resource-link-type" style={labelStyle}>
          Link type
        </label>
        <input
          id="resource-link-type"
          type="text"
          value={linkType}
          onChange={(e) => setLinkType(e.target.value)}
          placeholder="e.g. Course syllabus, Financial aid…"
          style={inputStyle}
          autoComplete="off"
        />
      </div>

      <div style={{ marginBottom: '12px' }}>
        <label htmlFor="resource-link-url" style={labelStyle}>
          Link
        </label>
        <input
          id="resource-link-url"
          type="url"
          inputMode="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://"
          style={{
            ...inputStyle,
            borderColor: urlTouchedInvalid ? '#dc2626' : '#e5e7eb',
          }}
          autoComplete="off"
        />
        {urlTouchedInvalid ? (
          <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#dc2626' }}>
            Enter a valid URL starting with http:// or https://
          </p>
        ) : null}
      </div>

      {urlValid ? (
        <div style={{ marginBottom: '16px' }}>
          <a
            href={urlNormalized}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '8px',
              backgroundColor: '#2563eb',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            <LinkIcon sx={{ fontSize: '18px' }} />
            View Link
          </a>
        </div>
      ) : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => {
            setLinkType('');
            setUrl('');
            onClose?.();
          }}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            backgroundColor: '#fff',
            color: '#374151',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: !canSave ? '#93c5fd' : '#2563eb',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 600,
            cursor: !canSave ? 'not-allowed' : 'pointer',
            opacity: !canSave ? 0.9 : 1,
          }}
        >
          <LinkIcon sx={{ fontSize: '18px' }} />
          {saving ? 'Saving…' : 'Save link'}
        </button>
      </div>
    </div>
  );
};

export default ResourceLinkFormDropdown;
